import type { Graphics } from "pixi.js";
import type { ProjectileImpactProfile } from "../champions/projectileVisuals.js";

export type ProjectileImpactKind =
  "damage" | "absorb" | "immune" | "reflect" | "terrain" | "zone" | "neutral" | "origin" | "expiry";

/** Screen-pixel accents, never world-space collision or danger footprints. */
export const PROJECTILE_IMPACT_STYLES = {
  damage: { lifetimeMs: 260, startRadiusPx: 18, endRadiusPx: 32 },
  absorb: { lifetimeMs: 280, startRadiusPx: 20, endRadiusPx: 34 },
  immune: { lifetimeMs: 240, startRadiusPx: 18, endRadiusPx: 18 },
  reflect: { lifetimeMs: 220, startRadiusPx: 16, endRadiusPx: 16 },
  terrain: { lifetimeMs: 220, startRadiusPx: 14, endRadiusPx: 24 },
  zone: { lifetimeMs: 180, startRadiusPx: 14, endRadiusPx: 4 },
  neutral: { lifetimeMs: 180, startRadiusPx: 10, endRadiusPx: 18 },
  origin: { lifetimeMs: 180, startRadiusPx: 12, endRadiusPx: 24 },
  expiry: { lifetimeMs: 80, startRadiusPx: 4, endRadiusPx: 0 },
} as const;

const WHITE = 0xffffff;
const PALE = 0xdce8f0;
const SHIELD_CYAN = 0x78e6ff;
const AXES = [
  [1, 0],
  [0, 1],
  [-1, 0],
  [0, -1],
] as const;

function regularPolygon(count: number, radius: number): number[] {
  const points: number[] = [];
  for (let i = 0; i < count; i++) {
    const angle = (i * Math.PI * 2) / count;
    points.push(Math.cos(angle) * radius, Math.sin(angle) * radius);
  }
  return points;
}

// Fixed polygon counts keep every accent within Pixi's automatic batching
// budget. Geometry is built once at spawn, then transformed without redrawing.
const RING = regularPolygon(20, 1);
const GLOW = regularPolygon(12, 0.56);
const CENTER = regularPolygon(8, 0.19);
const DOT = regularPolygon(10, 1);

function drawDamage(g: Graphics, profile: ProjectileImpactProfile): void {
  if (profile.preset === "fireBurst") {
    g.poly(GLOW).fill({ color: profile.accentColor, alpha: 0.23 });
  }
  for (const [dx, dy] of AXES) {
    const px = -dy;
    const py = dx;
    if (profile.preset === "iceShards") {
      // Four angular ice fragments, each with a bright facet and chipped base.
      g.poly([
        dx,
        dy,
        dx * 0.49 + px * 0.17,
        dy * 0.49 + py * 0.17,
        dx * 0.26,
        dy * 0.26,
        dx * 0.44 - px * 0.12,
        dy * 0.44 - py * 0.12,
      ]).fill({ color: profile.color });
      g.poly([dx, dy, dx * 0.49 + px * 0.17, dy * 0.49 + py * 0.17, dx * 0.43, dy * 0.43]).fill({
        color: WHITE,
        alpha: 0.7,
      });
    } else if (profile.preset === "fireBurst") {
      // The four-ray silhouette stays legible beneath the small flame lobes.
      g.poly([
        dx,
        dy,
        dx * 0.5 + px * 0.21,
        dy * 0.5 + py * 0.21,
        dx * 0.14,
        dy * 0.14,
        dx * 0.53 - px * 0.14,
        dy * 0.53 - py * 0.14,
      ]).fill({ color: profile.color });
      const ex = (dx + px) * 0.55;
      const ey = (dy + py) * 0.55;
      g.poly([ex - 0.06, ey, ex, ey - 0.08, ex + 0.06, ey, ex, ey + 0.08]).fill({
        color: profile.accentColor,
        alpha: 0.8,
      });
    } else {
      g.poly([
        dx,
        dy,
        dx * 0.32 + px * 0.1,
        dy * 0.32 + py * 0.1,
        dx * 0.16,
        dy * 0.16,
        dx * 0.32 - px * 0.1,
        dy * 0.32 - py * 0.1,
      ]).fill({ color: profile.color });
    }
  }
  g.poly(CENTER).fill({ color: WHITE, alpha: 0.85 });
}

/** Draw normalized local geometry. The manager animates its screen-space scale. */
export function drawProjectileImpactGraphics(
  g: Graphics,
  kind: ProjectileImpactKind,
  profile: ProjectileImpactProfile,
): void {
  g.clear();
  g.label = `projectile-impact:${kind}`;
  switch (kind) {
    case "damage":
      drawDamage(g, profile);
      break;
    case "absorb":
      g.poly(RING).stroke({ color: SHIELD_CYAN, width: 0.13, join: "bevel" });
      break;
    case "immune":
      g.moveTo(-1, -1)
        .lineTo(1, 1)
        .moveTo(-1, 1)
        .lineTo(1, -1)
        .stroke({ color: WHITE, width: 0.23, cap: "round" });
      break;
    case "reflect": {
      // A fixed ten-segment return curve avoids radius-dependent tessellation.
      const start = Math.PI * 0.2;
      for (let i = 0; i <= 10; i++) {
        const angle = start - (i / 10) * Math.PI * 1.25;
        const x = Math.cos(angle) * 0.78;
        const y = Math.sin(angle) * 0.78;
        if (i === 0) g.moveTo(x, y);
        else g.lineTo(x, y);
      }
      g.stroke({ color: WHITE, width: 0.18, join: "bevel" });
      g.moveTo(-0.97, -0.11)
        .lineTo(-0.77, 0.13)
        .lineTo(-0.48, -0.06)
        .stroke({ color: WHITE, width: 0.18, join: "bevel" });
      break;
    }
    case "terrain":
      g.moveTo(-0.28, -0.12)
        .lineTo(-0.86, -0.52)
        .moveTo(0, -0.3)
        .lineTo(0.1, -1)
        .moveTo(0.3, -0.05)
        .lineTo(0.91, -0.29)
        .stroke({ color: PALE, width: 0.16, cap: "round" });
      break;
    case "zone":
      g.poly(RING).stroke({ color: PALE, width: 0.14, join: "bevel" });
      break;
    case "neutral":
      g.poly(RING).stroke({ color: PALE, width: 0.16, join: "bevel" });
      break;
    case "origin":
      g.poly(RING)
        .fill({ color: profile.color, alpha: 0.12 })
        .stroke({ color: profile.color, alpha: 0.7, width: 0.12, join: "bevel" });
      break;
    case "expiry":
      g.poly(DOT).fill({ color: PALE, alpha: 0.45 });
      break;
  }
}
